<template>
  <div class="revisions-page">
    <div class="page-header">
      <div>
        <h2 style="margin-left: 0px;">{{ title }}</h2>
        <p class="page-subtitle" v-if="!loading">
          {{ items.length.toLocaleString() }} changes across {{ totalRecordCount.toLocaleString() }} records
          <span v-if="latestChange">· last change {{ relativeTime(latestChange.time) }}</span>
        </p>
      </div>
      <button type="button" class="secondary-button" :disabled="loading" @click="$emit('refresh')">
        <font-awesome-icon :icon="['fas', 'rotate-right']" :spin="loading" /> Refresh
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-panel">
      <div class="filter-row">
        <div class="search-box">
          <font-awesome-icon class="search-icon" :icon="['fas', 'magnifying-glass']" />
          <input type="text" v-model="searchInput"
            placeholder="Search fields, values, users or record IDs…" aria-label="Search revisions" />
          <button v-if="searchInput" type="button" class="search-clear" title="Clear search" @click="searchInput = ''">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <label class="filter-field">
          <span>When</span>
          <select v-model="filters.range">
            <option value="all">Any time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        <template v-if="filters.range === 'custom'">
          <label class="filter-field">
            <span>From</span>
            <input type="date" v-model="filters.from" :max="filters.to || undefined" />
          </label>
          <label class="filter-field">
            <span>To</span>
            <input type="date" v-model="filters.to" :min="filters.from || undefined" />
          </label>
        </template>

        <label class="filter-field">
          <span>Sort</span>
          <select v-model="sortOrder">
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label>

        <div class="view-toggle" role="group" aria-label="View">
          <button type="button" :class="{ active: view === 'timeline' }" @click="view = 'timeline'">
            <font-awesome-icon :icon="['fas', 'list']" /> Timeline
          </button>
          <button type="button" :class="{ active: view === 'records' }" @click="view = 'records'">
            <font-awesome-icon :icon="['fas', 'layer-group']" /> By record
          </button>
        </div>
      </div>

      <div class="type-chips" role="group" aria-label="Filter by type">
        <button type="button" class="type-chip" :class="{ active: !filters.types.length }" @click="filters.types = []">
          All types <span class="chip-count">{{ typeCountTotal.toLocaleString() }}</span>
        </button>
        <button v-for="option in typeOptions" :key="option.type" type="button" class="type-chip"
          :class="{ active: filters.types.includes(option.type), empty: !option.count }"
          :style="typeStyle(option.type)" @click="toggleType(option.type)">
          {{ option.type }} <span class="chip-count">{{ option.count.toLocaleString() }}</span>
        </button>
      </div>

      <div class="filter-row secondary">
        <label class="filter-field">
          <span>Action</span>
          <select v-model="filters.action">
            <option value="">All actions</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
          </select>
        </label>
        <label class="filter-field">
          <span>Field</span>
          <select v-model="filters.field">
            <option value="">All fields</option>
            <option v-for="option in fieldOptions" :key="option.key" :value="option.key">
              {{ option.key }} ({{ option.count }})
            </option>
          </select>
        </label>
        <label class="filter-field">
          <span>Changed by</span>
          <select v-model="filters.user">
            <option value="">Anyone</option>
            <option v-for="option in userOptions" :key="option.id" :value="option.id">
              {{ option.name }} ({{ option.count }})
            </option>
          </select>
        </label>
        <label class="filter-field narrow">
          <span>Record ID</span>
          <input type="text" v-model.trim="filters.recordId" placeholder="Any" inputmode="numeric" />
        </label>

        <button type="button" class="clear-filter-button" :disabled="!activeFilterCount" @click="clearFilters">
          Clear filters<span v-if="activeFilterCount"> ({{ activeFilterCount }})</span>
        </button>
      </div>
    </div>

    <!-- Results summary + pagination -->
    <div class="results-bar">
      <div class="results-info">
        <template v-if="loading">Loading revisions…</template>
        <template v-else-if="!pageTotal">No matching revisions</template>
        <template v-else>
          Showing <strong>{{ (pageStart + 1).toLocaleString() }}–{{ pageEnd.toLocaleString() }}</strong>
          of <strong>{{ pageTotal.toLocaleString() }}</strong>
          {{ view === 'timeline' ? 'changes' : 'records' }}
          <span v-if="view === 'records'" class="muted">({{ filteredRevisions.length.toLocaleString() }} changes)</span>
        </template>
      </div>
      <Pagination v-if="totalPages > 1" :totalPages="totalPages" :currentPage="currentPage" @page-change="onPageChange" />
    </div>

    <div v-if="error" class="state-box error">{{ error }}</div>
    <div v-else-if="loading" class="state-box">Loading revisions…</div>
    <div v-else-if="!pageTotal" class="state-box">
      No revisions match these filters.
      <button v-if="activeFilterCount" type="button" class="link-button" @click="clearFilters">Clear filters</button>
    </div>

    <!-- Timeline view -->
    <div v-else-if="view === 'timeline'" class="table-wrap">
      <RevisionTable :items="pagedRevisions" :now="now" show-record group-by-day linkable
        @focus-record="focusRecord" @focus-user="focusUser" />
    </div>

    <!-- Grouped by record view -->
    <div v-else class="record-list">
      <div v-for="record in pagedRecords" :key="record.key" class="record-card">
        <button type="button" class="record-header" @click="toggleRecord(record.key)">
          <span class="type-badge" :style="typeStyle(record.type)">{{ record.type }}</span>
          <span class="record-title">#{{ record.id }}</span>
          <span v-if="record.latest.label" class="record-label muted">{{ record.latest.label }}</span>
          <span v-if="record.created" class="action-badge created">created</span>
          <span v-if="record.updated" class="action-badge updated">updated</span>
          <span v-if="record.deleted" class="action-badge deleted">deleted</span>
          <span class="record-meta">
            {{ record.changes.length }} {{ record.changes.length === 1 ? 'change' : 'changes' }}
            · last {{ relativeTime(record.latest.time) }} by {{ record.latest.user_name || 'System' }}
          </span>
          <font-awesome-icon class="expand-icon" :icon="['fas', openRecords[record.key] ? 'chevron-up' : 'chevron-down']" />
        </button>

        <RevisionTable v-if="openRecords[record.key]" class="nested" :items="record.changes" :now="now" />
      </div>
    </div>

    <div v-if="totalPages > 1 && !loading" class="results-bar bottom">
      <div></div>
      <Pagination :totalPages="totalPages" :currentPage="currentPage" @page-change="onPageChange" />
    </div>
  </div>
</template>

<script>
import Pagination from './TablePagination.vue';
import RevisionTable from './RevisionTable.vue';
import { DAY_MS, normaliseRevisions, relativeTime, typeStyle } from './revisionUtils';

const RANGE_MS = { '24h': DAY_MS, '7d': 7 * DAY_MS, '30d': 30 * DAY_MS };

const DEFAULT_FILTERS = () => ({
  types: [],
  action: '',
  field: '',
  user: '',
  recordId: '',
  search: '',
  range: 'all',
  from: '',
  to: '',
});

// Filterable list of revision rows from /api/revisions, with timeline and per-record views.
// The parent page fetches and scopes the rows; filters are kept in the URL query.
export default {
  name: 'RevisionBrowser',
  components: {
    Pagination,
    RevisionTable,
  },

  props: {
    title: { type: String, default: 'Revisions' },
    revisions: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    // Optional extra name per record, e.g. { 'Task#12': '05-10-00 · Engine run' }
    recordLabels: { type: Object, default: () => ({}) },
  },
  emits: ['refresh'],

  data() {
    return {
      filters: DEFAULT_FILTERS(),
      searchInput: '',
      sortOrder: 'desc',
      view: 'timeline',
      currentPage: 1,
      perPage: 50,
      openRecords: {},
      now: Date.now(),
    };
  },

  computed: {
    // Precompute everything the filters need once, so filtering stays fast on large histories
    items() {
      return normaliseRevisions(this.revisions, this.recordLabels);
    },
    totalRecordCount() {
      return new Set(this.items.map(r => r.recordKey)).size;
    },
    latestChange() {
      return this.items[0] || null;
    },

    dateBounds() {
      const { range, from, to } = this.filters;
      if (RANGE_MS[range]) return { min: this.now - RANGE_MS[range], max: Infinity };
      if (range === 'custom') {
        return {
          min: from ? new Date(`${from}T00:00:00`).getTime() : -Infinity,
          max: to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity,
        };
      }
      return { min: -Infinity, max: Infinity };
    },

    // Every filter except type, so the type chips can show how many matches each type would have
    filteredIgnoringType() {
      const { action, field, user, recordId } = this.filters;
      const search = this.filters.search.toLowerCase();
      const { min, max } = this.dateBounds;

      return this.items.filter(r =>
        (!action || r.action === action) &&
        (!field || r.key === field) &&
        (!user || r.userKey === user) &&
        (!recordId || String(r.revisionable_id) === recordId) &&
        r.time >= min && r.time <= max &&
        (!search || r.haystack.includes(search))
      );
    },
    filteredRevisions() {
      const types = this.filters.types;
      const filtered = types.length
        ? this.filteredIgnoringType.filter(r => types.includes(r.revisionable_type))
        : this.filteredIgnoringType;
      // revisions are stored newest first
      return this.sortOrder === 'asc' ? filtered.slice().reverse() : filtered;
    },

    typeOptions() {
      const counts = {};
      this.items.forEach(r => { counts[r.revisionable_type] = 0; });
      this.filteredIgnoringType.forEach(r => { counts[r.revisionable_type]++; });
      return Object.keys(counts).sort().map(type => ({ type, count: counts[type] }));
    },
    typeCountTotal() {
      return this.filteredIgnoringType.length;
    },
    // Fields that exist on the selected types
    fieldOptions() {
      const types = this.filters.types;
      const counts = {};
      this.items.forEach(r => {
        if (r.action !== 'updated') return;
        if (types.length && !types.includes(r.revisionable_type)) return;
        counts[r.key] = (counts[r.key] || 0) + 1;
      });
      return Object.keys(counts).sort().map(key => ({ key, count: counts[key] }));
    },
    userOptions() {
      const users = {};
      this.items.forEach(r => {
        if (!users[r.userKey]) users[r.userKey] = { id: r.userKey, name: r.user_name || 'System', count: 0 };
        users[r.userKey].count++;
      });
      return Object.values(users).sort((a, b) => a.name.localeCompare(b.name));
    },

    activeFilterCount() {
      const f = this.filters;
      return (f.types.length ? 1 : 0) + [f.action, f.field, f.user, f.recordId, f.search]
        .filter(Boolean).length + (f.range !== 'all' ? 1 : 0);
    },

    groupedRecords() {
      const records = new Map();
      this.filteredRevisions.forEach(r => {
        let record = records.get(r.recordKey);
        if (!record) {
          record = {
            key: r.recordKey, type: r.revisionable_type, id: r.revisionable_id,
            changes: [], latest: r, created: false, updated: false, deleted: false,
          };
          records.set(r.recordKey, record);
        }
        record.changes.push(r);
        if (r.time > record.latest.time) record.latest = r;
        if (r.action === 'created') record.created = true;
        if (r.action === 'updated') record.updated = true;
        if (r.action === 'deleted') record.deleted = true;
      });
      const direction = this.sortOrder === 'asc' ? 1 : -1;
      return [...records.values()].sort((a, b) => direction * (a.latest.time - b.latest.time));
    },

    pageTotal() {
      return this.view === 'timeline' ? this.filteredRevisions.length : this.groupedRecords.length;
    },
    totalPages() {
      return Math.ceil(this.pageTotal / this.perPage);
    },
    pageStart() {
      return (this.currentPage - 1) * this.perPage;
    },
    pageEnd() {
      return Math.min(this.pageStart + this.perPage, this.pageTotal);
    },

    pagedRevisions() {
      return this.filteredRevisions.slice(this.pageStart, this.pageEnd);
    },
    pagedRecords() {
      return this.groupedRecords.slice(this.pageStart, this.pageEnd);
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.syncQuery();
      },
    },
    searchInput(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.filters.search = value.trim(); }, 250);
    },
    sortOrder() {
      this.currentPage = 1;
      this.syncQuery();
    },
    view() {
      this.currentPage = 1;
      this.syncQuery();
    },
    revisions() {
      this.now = Date.now();
    },
  },

  created() {
    this.readQuery();
  },

  mounted() {
    // Keeps "x min ago" labels and the relative date ranges fresh
    this.clock = setInterval(() => { this.now = Date.now(); }, 60 * 1000);
  },

  beforeUnmount() {
    clearInterval(this.clock);
    clearTimeout(this.searchTimer);
  },

  methods: {
    readQuery() {
      const q = this.$route.query;
      const filters = DEFAULT_FILTERS();
      if (q.types) filters.types = String(q.types).split(',').filter(Boolean);
      ['action', 'field', 'user', 'recordId', 'search', 'from', 'to'].forEach(key => {
        if (q[key]) filters[key] = String(q[key]);
      });
      if (q.range) filters.range = String(q.range);
      this.filters = filters;
      this.searchInput = filters.search;
      if (q.sort === 'asc') this.sortOrder = 'asc';
      if (q.view === 'records') this.view = 'records';
    },
    syncQuery() {
      const f = this.filters;
      const query = {};
      if (f.types.length) query.types = f.types.join(',');
      ['action', 'field', 'user', 'recordId', 'search'].forEach(key => { if (f[key]) query[key] = f[key]; });
      if (f.range !== 'all') query.range = f.range;
      if (f.range === 'custom') {
        if (f.from) query.from = f.from;
        if (f.to) query.to = f.to;
      }
      if (this.sortOrder === 'asc') query.sort = 'asc';
      if (this.view === 'records') query.view = 'records';
      this.$router.replace({ query }).catch(() => {});
    },

    toggleType(type) {
      const types = this.filters.types;
      this.filters.types = types.includes(type) ? types.filter(t => t !== type) : [...types, type];
    },
    focusRecord(item) {
      this.filters.types = [item.revisionable_type];
      this.filters.recordId = String(item.revisionable_id);
    },
    focusUser(item) {
      this.filters.user = item.userKey;
    },
    clearFilters() {
      this.filters = DEFAULT_FILTERS();
      this.searchInput = '';
    },
    onPageChange(page) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    toggleRecord(key) {
      this.openRecords = { ...this.openRecords, [key]: !this.openRecords[key] };
    },

    typeStyle,
    relativeTime(time) {
      return relativeTime(time, this.now);
    },
  },
};
</script>

<style scoped>
.revisions-page {
  padding: 10px 2% 20px;
  text-align: left;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
}

/* Long product names wrap within the title instead of squashing the Refresh button */
.page-header > div {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

h1 {
  margin: 0;
}

.page-subtitle {
  margin: 4px 0 0;
  color: #666;
  font-size: 14px;
}

.muted {
  color: #6b7280;
}

.small {
  font-size: 12px;
}

/* Filters */
.filter-panel {
  background: #fff;
  border: 1px solid #d0d5dc;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.filter-row.secondary {
  padding-top: 12px;
  border-top: 1px solid #eef0f3;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 0;
  font-size: 12px;
  color: #555;
  font-weight: 600;
}

.filter-field select,
.filter-field input {
  height: 34px;
  min-width: 150px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  font-weight: normal;
  background: #fff;
}

.filter-field.narrow input {
  min-width: 0;
  width: 100px;
}

.search-box {
  position: relative;
  flex: 1 1 320px;
  min-width: 240px;
}

.search-box input {
  width: 100%;
  height: 34px;
  padding: 4px 32px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
}

.search-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #888;
  padding: 2px 6px;
}

.view-toggle {
  display: inline-flex;
  border: 1px solid #200d0d;
  border-radius: 5px;
  overflow: hidden;
  margin-left: auto;
}

.view-toggle button {
  height: 34px;
  padding: 0 12px;
  border: none;
  background: #fff;
  color: #463333;
  font-size: 14px;
}

.view-toggle button.active {
  background: #463333;
  color: #fff;
}

.type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.type-chip {
  --type-hue: 0;
  border: 1px solid #cfd4da;
  background: #fff;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 13px;
  color: #333;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.type-chip:hover {
  border-color: #463333;
}

.type-chip.empty {
  opacity: 0.5;
}

.type-chip.active {
  background: #463333;
  border-color: #463333;
  color: #fff;
  opacity: 1;
}

.chip-count {
  font-size: 11px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
}

.type-chip.active .chip-count {
  background: rgba(255, 255, 255, 0.2);
}

.secondary-button,
.clear-filter-button {
  height: 34px;
  padding: 0 14px;
  border-radius: 5px;
  font-size: 14px;
}

.secondary-button {
  background-color: #463333;
  color: #fff;
  border: 1px solid #200d0d;
  flex-shrink: 0;
  white-space: nowrap;
}

.secondary-button:hover:not(:disabled) {
  background-color: #2b1616;
}

.clear-filter-button {
  background-color: #dee2e6;
  border: 1px solid #999;
  margin-left: auto;
}

.clear-filter-button:hover:not(:disabled) {
  background-color: #cecece;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Results */
.results-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 18px 0 10px;
  font-size: 14px;
  color: #444;
}

.results-bar.bottom {
  margin-top: 16px;
}

.state-box {
  padding: 30px;
  text-align: center;
  border: 1px dashed #ccc;
  border-radius: 8px;
  color: #666;
}

.state-box.error {
  color: #b42318;
  border-color: #f0b4ae;
  background: #fff5f4;
}

.link-button {
  border: none;
  background: none;
  color: #1d4ed8;
  text-decoration: underline;
  padding: 0 4px;
}

/* Tables */
.table-wrap {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow-x: auto;
}

.expand-icon {
  color: #888;
}

.record-label {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-badge {
  --type-hue: 0;
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 4px;
  margin-right: 4px;
  color: hsl(var(--type-hue), 55%, 28%);
  background: hsl(var(--type-hue), 70%, 93%);
  border: 1px solid hsl(var(--type-hue), 45%, 80%);
}

.action-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 7px;
  border-radius: 4px;
}

.action-badge.created {
  background: #e6f4ea;
  color: #1e6b34;
}

.action-badge.updated {
  background: #fef7e0;
  color: #8a6100;
}

.action-badge.deleted {
  background: #fdecea;
  color: #a1261b;
}

/* Record view */
.record-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.record-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f7f7f7;
  border: none;
  text-align: left;
}

.record-header:hover {
  background: #efefef;
}

.record-title {
  font-weight: 600;
  font-size: 16px;
}

.record-meta {
  margin-left: auto;
  font-size: 13px;
  color: #666;
}

.nested {
  border-top: 1px solid #ddd;
}

@media (max-width: 800px) {
  .view-toggle,
  .clear-filter-button {
    margin-left: 0;
  }

  .record-meta {
    display: none;
  }
}
</style>
